import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    IconBuilding,
    IconPlus,
    IconSettings,
    IconLogout,
    IconUsers,
    IconCalendar,
    IconCheck,
    IconUserPlus,
    IconCopy,
} from '@tabler/icons-react';
import { notifications } from '../../../lib/notifications';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/dialog';
import { useOfficeSpaceStore } from '../../../store/officeSpaceStore';
import { useAuthStore } from '../../../store/authStore';
import { useTranslation } from '../../../i18n/hooks/useTranslation';

export default function OfficeSpaceList() {
    const navigate = useNavigate();
    const { t } = useTranslation();
    const { user } = useAuthStore();

    const {
        officeSpaces,
        selectedOfficeSpace,
        fetchOfficeSpaces,
        selectOfficeSpace,
        createOfficeSpace,
        leaveOfficeSpace,
        isLoading,
    } = useOfficeSpaceStore();

    const isOwnerRole = user?.role === 'OWNER';
    const isOperator = user?.role === 'OPERATOR';

    // Create modal
    const [createOpen, setCreateOpen] = useState(false);
    const [newOfficeName, setNewOfficeName] = useState('');
    const [creating, setCreating] = useState(false);

    // Leave modal (operators)
    const [leaveOpen, setLeaveOpen] = useState(false);
    const [officeToLeave, setOfficeToLeave] = useState(null);

    // Switch-shop confirmation
    const [switchOpen, setSwitchOpen] = useState(false);
    const [officeToSwitch, setOfficeToSwitch] = useState(null);

    // Copy operator registration link
    const [copied, setCopied] = useState(false);

    const handleCopyOperatorLink = async () => {
        const link = `${window.location.origin}/register/operator`;
        try {
            await navigator.clipboard.writeText(link);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
            notifications.show({
                title: t('common.success'),
                message: t('officeSpace.operatorLinkCopied'),
                color: 'green',
            });
        } catch {
            notifications.show({
                title: t('common.error'),
                message: link,
                color: 'red',
            });
        }
    };

    useEffect(() => {
        fetchOfficeSpaces();
    }, []);

    const handleSelect = (os) => {
        selectOfficeSpace(os);
        notifications.show({
            title: t('officeSpace.officeSpaceSwitched'),
            message: t('officeSpace.switchedTo', { name: os.name }),
            color: 'blue',
        });
    };

    const confirmSwitch = () => {
        if (officeToSwitch) handleSelect(officeToSwitch);
        setSwitchOpen(false);
        setOfficeToSwitch(null);
    };

    const handleCreate = async () => {
        if (!newOfficeName.trim()) {
            notifications.show({
                title: t('common.error'),
                message: t('officeSpace.pleaseEnterOfficeSpaceName'),
                color: 'red',
            });
            return;
        }
        setCreating(true);
        try {
            await createOfficeSpace({ name: newOfficeName.trim() });
            notifications.show({
                title: t('common.success'),
                message: t('officeSpace.officeSpaceCreated'),
                color: 'green',
            });
            setCreateOpen(false);
            setNewOfficeName('');
        } catch (err) {
            notifications.show({
                title: t('common.error'),
                message: err.message || t('officeSpace.failedToCreateOfficeSpace'),
                color: 'red',
            });
        } finally {
            setCreating(false);
        }
    };

    const handleLeave = async () => {
        if (!officeToLeave) return;
        try {
            await leaveOfficeSpace(officeToLeave.id);
            notifications.show({
                title: t('common.success'),
                message: t('officeSpace.leftOfficeSpace', { name: officeToLeave.name }),
                color: 'green',
            });
            setLeaveOpen(false);
            setOfficeToLeave(null);
        } catch (err) {
            notifications.show({
                title: t('common.error'),
                message: err.message || t('officeSpace.failedToLeaveOfficeSpace'),
                color: 'red',
            });
        }
    };

    return (
        <div className="w-full font-hind space-y-5 sm:space-y-6 py-2 sm:py-4">
            {/* Header */}
            <div className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
                <div>
                    <h1 className="text-xl sm:text-2xl font-bold tracking-tight text-zinc-900 dark:text-zinc-100">
                        {t('officeSpace.listTitle')}
                    </h1>
                    <p className="mt-1 text-sm text-zinc-500 dark:text-zinc-400">
                        {t(isOperator ? 'officeSpace.operatorListSubtitle' : 'officeSpace.listSubtitle')}
                    </p>
                </div>
                <div className="flex items-center gap-2 flex-shrink-0">
                    {isOwnerRole && (
                        <button
                            onClick={() => setCreateOpen(true)}
                            className="flex items-center gap-1.5 px-4 py-2.5 bg-zinc-900 hover:bg-zinc-800 dark:bg-zinc-100 dark:hover:bg-white text-white dark:text-zinc-900 text-[13px] font-semibold rounded-lg transition-colors"
                        >
                            <IconPlus size={16} stroke={1.8} />
                            {t('officeSpace.createNewOfficeSpace')}
                        </button>
                    )}
                </div>
            </div>

            {/* Loading */}
            {isLoading && officeSpaces.length === 0 ? (
                <div className="flex items-center justify-center py-16">
                    <span className="w-6 h-6 border-2 border-primary border-t-transparent rounded-full animate-spin" />
                </div>
            ) : officeSpaces.length === 0 ? (
                /* Empty state */
                <div className="flex flex-col items-center justify-center rounded-2xl border border-dashed border-zinc-300 dark:border-zinc-700 bg-white dark:bg-zinc-900 py-16 px-4 text-center">
                    <span className="flex h-14 w-14 items-center justify-center rounded-2xl bg-primary/10 text-primary">
                        <IconBuilding size={28} stroke={1.6} />
                    </span>
                    <h3 className="mt-4 text-base font-semibold text-zinc-900 dark:text-zinc-100">
                        {t(isOperator ? 'officeSpace.operatorEmptyTitle' : 'officeSpace.emptyTitle')}
                    </h3>
                    <p className="mt-1 max-w-sm text-sm text-zinc-500 dark:text-zinc-400">
                        {t(isOperator ? 'officeSpace.operatorEmptyDescription' : 'officeSpace.emptyDescription')}
                    </p>
                    {isOwnerRole && (
                        <button
                            onClick={() => setCreateOpen(true)}
                            className="mt-5 flex items-center gap-1.5 px-4 py-2.5 bg-primary hover:bg-primary/90 text-white text-[13px] font-semibold rounded-lg transition-colors"
                        >
                            <IconPlus size={16} stroke={1.8} />
                            {t('officeSpace.createNewOfficeSpace')}
                        </button>
                    )}
                </div>
            ) : (
                /* Grid of office space cards */
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2 xl:grid-cols-3">
                    {officeSpaces.map((os) => {
                        const isSelected = selectedOfficeSpace?.id === os.id;
                        const isActive = os.status === 'active';
                        const operatorCount = os.operators?.length || 0;

                        return (
                            <div
                                key={os.id}
                                className={`flex flex-col rounded-2xl border p-5 transition-all ${
                                    isSelected
                                        ? 'border-primary ring-2 ring-primary/40 bg-primary/[0.04] dark:bg-primary/10 shadow-md shadow-primary/10'
                                        : 'border-zinc-200 dark:border-zinc-700 bg-white dark:bg-zinc-900 shadow-sm hover:border-primary/40'
                                }`}
                            >
                                {/* Top: icon + name + status */}
                                <div className="flex items-start gap-3">
                                    <span className="flex h-10 w-10 flex-none items-center justify-center rounded-xl bg-primary/10 text-primary">
                                        <IconBuilding size={20} stroke={1.7} />
                                    </span>
                                    <div className="min-w-0 flex-1">
                                        <div className="flex items-center gap-2">
                                            <h3 className="truncate text-[15px] font-semibold text-zinc-900 dark:text-zinc-100">
                                                {os.name}
                                            </h3>
                                            {/* Active status as a simple green dot beside the title */}
                                            <span
                                                className={`h-2 w-2 flex-none rounded-full ${
                                                    isActive ? 'bg-green-500' : 'bg-zinc-300 dark:bg-zinc-600'
                                                }`}
                                                title={isActive ? t('officeSpace.active') : t('officeSpace.inactive')}
                                            />
                                            {os.isDefault && isOwnerRole && (
                                                <span className="rounded bg-primary/10 px-1.5 py-0.5 text-[10px] font-semibold text-primary">
                                                    {t('officeSpace.default')}
                                                </span>
                                            )}
                                        </div>
                                        {os.businessProfile?.businessAddress && (
                                            <p className="mt-1 truncate text-[12.5px] text-zinc-500 dark:text-zinc-400">
                                                {os.businessProfile.businessAddress}
                                            </p>
                                        )}
                                    </div>
                                    {isSelected && (
                                        <span className="flex flex-none items-center gap-1 rounded-full bg-green-100 px-2 py-0.5 text-[11px] font-semibold text-green-700 dark:bg-green-900/30 dark:text-green-400">
                                            <IconCheck size={13} stroke={2.5} />
                                            {t('officeSpace.selected')}
                                        </span>
                                    )}
                                </div>

                                {/* Description */}
                                {os.metadata?.description && (
                                    <p className="mt-3 line-clamp-2 text-[13px] text-zinc-500 dark:text-zinc-400">
                                        {os.metadata.description}
                                    </p>
                                )}

                                {/* Meta */}
                                <div className="mt-4 flex flex-wrap items-center gap-x-4 gap-y-1.5 text-[12.5px] text-zinc-500 dark:text-zinc-400">
                                    {isOwnerRole && (
                                        <span className="inline-flex items-center gap-1.5">
                                            <IconUsers size={15} stroke={1.7} />
                                            {t('officeSpace.operatorsCount', { count: operatorCount })}
                                        </span>
                                    )}
                                    <span className="inline-flex items-center gap-1.5">
                                        <IconCalendar size={15} stroke={1.7} />
                                        {new Date(os.createdAt).toLocaleDateString()}
                                    </span>
                                </div>

                                {/* Actions */}
                                <div className="mt-4 flex items-center gap-2 border-t border-zinc-100 dark:border-zinc-800 pt-4">
                                    {isSelected ? (
                                        <span className="flex-1 inline-flex items-center justify-center gap-1.5 rounded-lg bg-primary px-3 py-2 text-center text-[13px] font-semibold text-white">
                                            <IconCheck size={15} stroke={2.2} />
                                            {t('officeSpace.currentlyActive')}
                                        </span>
                                    ) : (
                                        <button
                                            onClick={() => {
                                                setOfficeToSwitch(os);
                                                setSwitchOpen(true);
                                            }}
                                            className="flex-1 rounded-lg border border-primary/50 text-primary hover:bg-primary/10 px-3 py-2 text-[13px] font-semibold transition-colors"
                                        >
                                            {t('officeSpace.select')}
                                        </button>
                                    )}

                                    {isOwnerRole ? (
                                        <button
                                            onClick={() => navigate(`/officespaces/${os.id}/settings`)}
                                            title={t('officeSpace.workspaceSettings')}
                                            className="flex items-center gap-1.5 rounded-lg border border-zinc-200 dark:border-zinc-700 px-3 py-2 text-[13px] font-semibold text-zinc-700 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors"
                                        >
                                            <IconSettings size={15} stroke={1.7} />
                                            {t('officeSpace.manage')}
                                        </button>
                                    ) : (
                                        <button
                                            onClick={() => {
                                                setOfficeToLeave(os);
                                                setLeaveOpen(true);
                                            }}
                                            title={t('officeSpace.leaveOfficeSpace')}
                                            className="flex items-center gap-1.5 rounded-lg border border-red-200 dark:border-red-900/40 px-3 py-2 text-[13px] font-semibold text-red-600 hover:bg-red-50 dark:hover:bg-red-900/20 transition-colors"
                                        >
                                            <IconLogout size={15} stroke={1.7} />
                                            {t('officeSpace.leave')}
                                        </button>
                                    )}
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {/* Add operator info */}
            {isOwnerRole && (
                <div className="flex flex-col gap-4 rounded-2xl border border-primary/20 bg-primary/5 dark:border-primary/25 dark:bg-primary/10 p-5 sm:flex-row sm:items-center sm:justify-between">
                    <div className="flex items-start gap-3">
                        <span className="flex h-10 w-10 flex-none items-center justify-center rounded-xl bg-primary/10 text-primary">
                            <IconUserPlus size={20} stroke={1.7} />
                        </span>
                        <div>
                            <h3 className="text-[15px] font-semibold text-zinc-900 dark:text-zinc-100">
                                {t('officeSpace.operatorInfoTitle')}
                            </h3>
                            <p className="mt-1 max-w-xl text-[13px] leading-relaxed text-zinc-600 dark:text-zinc-400">
                                {t('officeSpace.operatorInfoDescription')}
                            </p>
                        </div>
                    </div>
                    <button
                        onClick={handleCopyOperatorLink}
                        className="inline-flex flex-none items-center justify-center gap-1.5 rounded-lg bg-primary hover:bg-primary/90 px-4 py-2.5 text-[13px] font-semibold text-white transition-colors"
                    >
                        {copied ? (
                            <IconCheck size={16} stroke={2} />
                        ) : (
                            <IconCopy size={16} stroke={1.8} />
                        )}
                        {copied ? t('officeSpace.operatorLinkCopied') : t('officeSpace.operatorInfoAction')}
                    </button>
                </div>
            )}

            {/* Create modal */}
            <Dialog
                open={createOpen}
                onOpenChange={(v) => {
                    setCreateOpen(v);
                    if (!v) setNewOfficeName('');
                }}
            >
                <DialogContent className="max-w-sm">
                    <DialogHeader>
                        <DialogTitle>{t('officeSpace.createNewOfficeSpace')}</DialogTitle>
                    </DialogHeader>
                    <div className="space-y-4">
                        <div>
                            <label className="mb-1 block text-sm font-medium text-zinc-700 dark:text-zinc-300">
                                {t('officeSpace.workspaceName')}
                            </label>
                            <input
                                type="text"
                                value={newOfficeName}
                                onChange={(e) => setNewOfficeName(e.target.value)}
                                onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
                                placeholder={t('officeSpace.enterOfficeSpaceName')}
                                autoFocus
                                className="w-full rounded-lg border border-zinc-300 dark:border-zinc-600 bg-white dark:bg-zinc-800 px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/40"
                            />
                        </div>
                        <div className="flex justify-end gap-2">
                            <button
                                onClick={() => setCreateOpen(false)}
                                className="rounded-lg border border-zinc-300 dark:border-zinc-600 px-4 py-2 text-[13px] font-semibold text-zinc-700 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors"
                            >
                                {t('common.cancel')}
                            </button>
                            <button
                                onClick={handleCreate}
                                disabled={creating}
                                className="inline-flex items-center gap-1.5 rounded-lg bg-primary hover:bg-primary/90 px-4 py-2 text-[13px] font-semibold text-white transition-colors disabled:opacity-50"
                            >
                                {creating && (
                                    <span className="h-3 w-3 rounded-full border-2 border-white border-t-transparent animate-spin" />
                                )}
                                {t('officeSpace.create')}
                            </button>
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Leave modal */}
            <Dialog
                open={leaveOpen}
                onOpenChange={(v) => {
                    setLeaveOpen(v);
                    if (!v) setOfficeToLeave(null);
                }}
            >
                <DialogContent className="max-w-sm">
                    <DialogHeader>
                        <DialogTitle>{t('officeSpace.confirmLeaveOfficeSpace')}</DialogTitle>
                    </DialogHeader>
                    <p className="text-sm text-zinc-600 dark:text-zinc-400">
                        {t('officeSpace.leaveOfficeSpaceWarning', { name: officeToLeave?.name })}
                    </p>
                    <div className="mt-4 flex justify-end gap-2">
                        <button
                            onClick={() => setLeaveOpen(false)}
                            className="rounded-lg border border-zinc-300 dark:border-zinc-600 px-4 py-2 text-[13px] font-semibold text-zinc-700 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors"
                        >
                            {t('common.cancel')}
                        </button>
                        <button
                            onClick={handleLeave}
                            disabled={isLoading}
                            className="inline-flex items-center gap-1.5 rounded-lg bg-red-600 hover:bg-red-700 px-4 py-2 text-[13px] font-semibold text-white transition-colors disabled:opacity-50"
                        >
                            {isLoading && (
                                <span className="h-3 w-3 rounded-full border-2 border-white border-t-transparent animate-spin" />
                            )}
                            {t('officeSpace.leave')}
                        </button>
                    </div>
                </DialogContent>
            </Dialog>

            {/* Switch-shop confirmation */}
            <Dialog
                open={switchOpen}
                onOpenChange={(v) => {
                    setSwitchOpen(v);
                    if (!v) setOfficeToSwitch(null);
                }}
            >
                <DialogContent className="max-w-sm">
                    <DialogHeader>
                        <DialogTitle>{t('officeSpace.confirmSwitchTitle')}</DialogTitle>
                    </DialogHeader>
                    <div className="flex items-start gap-2.5 rounded-xl border border-primary/20 bg-primary/5 dark:bg-primary/10 p-3">
                        <span className="flex h-8 w-8 flex-none items-center justify-center rounded-lg bg-primary/10 text-primary">
                            <IconBuilding size={16} stroke={1.8} />
                        </span>
                        <p className="text-sm text-zinc-600 dark:text-zinc-300">
                            {t('officeSpace.confirmSwitchMessage', { name: officeToSwitch?.name })}
                        </p>
                    </div>
                    <div className="mt-4 flex justify-end gap-2">
                        <button
                            onClick={() => setSwitchOpen(false)}
                            className="rounded-lg border border-zinc-300 dark:border-zinc-600 px-4 py-2 text-[13px] font-semibold text-zinc-700 dark:text-zinc-300 hover:bg-zinc-50 dark:hover:bg-zinc-800 transition-colors"
                        >
                            {t('common.cancel')}
                        </button>
                        <button
                            onClick={confirmSwitch}
                            className="inline-flex items-center gap-1.5 rounded-lg bg-primary hover:bg-primary/90 px-4 py-2 text-[13px] font-semibold text-white transition-colors"
                        >
                            <IconCheck size={15} stroke={2} />
                            {t('officeSpace.confirmSwitchAction')}
                        </button>
                    </div>
                </DialogContent>
            </Dialog>
        </div>
    );
}
